// Lexer und Parser der Suchsprache (ST-SEA-002, „PQL Schreibweisen“;
// CM-SEA-011, CM-SEA-012). Grammatik: docs/product/suchsprache-grammatik.md.
//
// Der Parser ist fehlertolerant: er liefert fuer JEDE Eingabe einen Teilbaum
// und Hinweise mit Zeichenbereich, wirft nie und bricht beim Tippen nicht.
// Ob ein Hinweis das Abschicken sperrt, sagt `blocking`; was ein Schnitt
// (S1) zusaetzlich nicht kann, prueft s1-form.ts, nicht dieser Parser.
//
// Operatorwoerter zaehlen nur grossgeschrieben (E3 a): „Mueller und Soehne“
// bleibt Freitext.

import type { FieldNode, QueryNode, QueryValue, Span, TextNode } from './baum.ts';
import { fieldByName, fold, isLaterField, similarFieldName } from './felder.ts';

export type HintCode =
  | 'quote_unclosed'
  | 'paren_unclosed'
  | 'paren_unexpected'
  | 'parens_empty'
  | 'value_missing'
  | 'operator_dangling'
  | 'field_unknown'
  | 'colon_text'
  | 'lowercase_operator'
  | 'field_later'
  | 'or_across_fields'
  | 'not_open_field'
  | 'field_twice'
  | 'several_values'
  | 'comparison_unsupported'
  | 'status_unsupported'
  | 'role_unknown'
  | 'postal_code_form'
  | 'locality_length'
  | 'text_split'
  | 'text_length'
  | 'no_search_reason'
  | 'query_too_long'
  | 'separator_missing';

/** Ein Hinweis zu einer Stelle im Suchtext. `quote` ist der Teiltext, den der Satz zitiert. */
export interface QueryHint {
  readonly code: HintCode;
  readonly span: Span;
  readonly quote: string;
  readonly blocking: boolean;
  /** Was der Knopf „… übernehmen“ an die Stelle von `span` setzt. */
  readonly suggestion?: string;
  /** Wovon der Satz spricht, etwa das Feld bei `several_values`. */
  readonly subject?: string;
}

export interface ParseResult {
  readonly tree: QueryNode | undefined;
  readonly hints: readonly QueryHint[];
}

type Token =
  | { readonly kind: 'word'; readonly span: Span }
  | { readonly kind: 'phrase'; readonly text: string; readonly closed: boolean; readonly span: Span }
  | { readonly kind: 'field'; readonly name: string; readonly span: Span }
  | { readonly kind: 'lparen' | 'rparen'; readonly span: Span }
  | { readonly kind: 'or' | 'and' | 'not'; readonly span: Span }
  | { readonly kind: 'cmp'; readonly op: 'gt' | 'gte' | 'lt' | 'lte'; readonly span: Span };

const OR_WORDS = new Set(['ODER', 'OR']);
const AND_WORDS = new Set(['UND', 'AND']);
const NOT_WORDS = new Set(['NICHT', 'NOT']);
const LOWERCASE_OPERATORS = new Map([
  ['oder', 'ODER'],
  ['und', 'UND'],
]);

function isSpace(character: string | undefined): boolean {
  return character !== undefined && /\s/u.test(character);
}

function endsWord(character: string | undefined): boolean {
  return character === undefined || isSpace(character) || character === '(' || character === ')' || character === '"';
}

export function lex(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const c = source[i];
    if (isSpace(c)) {
      i += 1;
      continue;
    }
    if (c === '(' || c === ')') {
      tokens.push({ kind: c === '(' ? 'lparen' : 'rparen', span: { from: i, to: i + 1 } });
      i += 1;
      continue;
    }
    if (c === '"') {
      let j = i + 1;
      let text = '';
      let closed = false;
      while (j < source.length) {
        // In Anfuehrungszeichen maskiert der Backslash genau zwei Zeichen:
        // sich selbst und das Anfuehrungszeichen. Sonst steht er woertlich.
        if (source[j] === '\\' && (source[j + 1] === '"' || source[j + 1] === '\\')) {
          text += source[j + 1];
          j += 2;
          continue;
        }
        if (source[j] === '"') {
          closed = true;
          j += 1;
          break;
        }
        text += source[j];
        j += 1;
      }
      tokens.push({ kind: 'phrase', text, closed, span: { from: i, to: j } });
      i = j;
      continue;
    }
    // `-` und `!` schliessen aus, wenn direkt etwas folgt, auch eine Klammer
    // oder ein Anfuehrungszeichen; allein stehend sind sie Text.
    const after = source[i + 1];
    if ((c === '-' || c === '!') && after !== undefined && !isSpace(after) && after !== ')') {
      tokens.push({ kind: 'not', span: { from: i, to: i + 1 } });
      i += 1;
      continue;
    }
    if (source.startsWith('||', i) || source.startsWith('&&', i)) {
      tokens.push({ kind: c === '|' ? 'or' : 'and', span: { from: i, to: i + 2 } });
      i += 2;
      continue;
    }
    const previous = tokens[tokens.length - 1];
    // Direkt hinter einer Feldangabe, auch nach Leerraum („ort: >Berlin“, M-02).
    const nachFeld = previous?.kind === 'field' && source.slice(previous.span.to, i).trim().length === 0;
    if ((c === '>' || c === '<') && nachFeld) {
      const withEquals = source[i + 1] === '=';
      const op = c === '>' ? (withEquals ? 'gte' : 'gt') : withEquals ? 'lte' : 'lt';
      tokens.push({ kind: 'cmp', op, span: { from: i, to: i + (withEquals ? 2 : 1) } });
      i += withEquals ? 2 : 1;
      continue;
    }
    let j = i;
    while (!endsWord(source[j])) j += 1;
    const word = source.slice(i, j);
    const colon = word.indexOf(':');
    // Ein Wert darf selbst einen Doppelpunkt tragen („rolle:Autor:in“,
    // Hausregel Doppelpunktform): getrennt wird nur am ersten.
    if (colon > 0 && !nachFeld) {
      // `name:` ist ein Feldtoken; was hinter dem Doppelpunkt steht, wird neu
      // gelesen (Wert, Klammer, Anfuehrungszeichen oder Vergleich).
      tokens.push({ kind: 'field', name: word.slice(0, colon), span: { from: i, to: i + colon + 1 } });
      i += colon + 1;
      continue;
    }
    if (OR_WORDS.has(word)) tokens.push({ kind: 'or', span: { from: i, to: j } });
    else if (AND_WORDS.has(word)) tokens.push({ kind: 'and', span: { from: i, to: j } });
    else if (NOT_WORDS.has(word)) tokens.push({ kind: 'not', span: { from: i, to: j } });
    else tokens.push({ kind: 'word', span: { from: i, to: j } });
    i = j;
  }
  return tokens;
}

class Parser {
  private position = 0;
  readonly hints: QueryHint[] = [];
  private readonly source: string;
  private readonly tokens: readonly Token[];

  constructor(source: string, tokens: readonly Token[]) {
    this.source = source;
    this.tokens = tokens;
  }

  private peek(offset = 0): Token | undefined {
    return this.tokens[this.position + offset];
  }

  private next(): Token | undefined {
    const token = this.tokens[this.position];
    this.position += 1;
    return token;
  }

  private quote(span: Span): string {
    return this.source.slice(span.from, span.to);
  }

  private hint(code: HintCode, span: Span, blocking: boolean, extra: { suggestion?: string; subject?: string } = {}): void {
    this.hints.push({ code, span, quote: this.quote(span), blocking, ...extra });
  }

  parseAll(): QueryNode | undefined {
    const parts: QueryNode[] = [];
    for (;;) {
      const part = this.parseOr();
      if (part !== undefined) parts.push(part);
      const token = this.peek();
      if (token === undefined) break;
      // Nur eine ueberzaehlige schliessende Klammer haelt die oberste Ebene an.
      this.next();
      this.hint('paren_unexpected', token.span, true);
    }
    return combine('and', parts);
  }

  private atEndOfGroup(): boolean {
    const token = this.peek();
    return token === undefined || token.kind === 'rparen' || token.kind === 'or';
  }

  private parseOr(): QueryNode | undefined {
    const branches: QueryNode[] = [];
    const first = this.parseAnd();
    if (first !== undefined) branches.push(first);
    while (this.peek()?.kind === 'or') {
      const operator = this.next() as Token;
      if (branches.length === 0 || this.atEndOfGroup()) {
        this.hint('operator_dangling', operator.span, true);
      }
      const branch = this.parseAnd();
      if (branch !== undefined) branches.push(branch);
    }
    return combine('or', branches);
  }

  private parseAnd(): QueryNode | undefined {
    const items: QueryNode[] = [];
    while (!this.atEndOfGroup()) {
      const token = this.peek() as Token;
      if (token.kind === 'and') {
        this.next();
        if (items.length === 0 || this.atEndOfGroup()) this.hint('operator_dangling', token.span, true);
        continue;
      }
      const previous = items[items.length - 1];
      if (previous !== undefined && 'field' in previous && token.kind === 'word') {
        const operator = LOWERCASE_OPERATORS.get(this.quote(token.span));
        if (operator !== undefined) {
          this.hint('lowercase_operator', token.span, false, { suggestion: operator });
        }
      }
      const item = this.parseNot();
      if (item === undefined) continue;
      // Freitext vor und nach einem Wort mit Doppelpunkt („Müller Firma:Nord“)
      // ist EIN Teilstring, wie zusammenhaengende Woerter sonst auch.
      if (
        previous !== undefined &&
        'text' in previous &&
        'text' in item &&
        !previous.quoted &&
        !item.quoted &&
        this.source.slice(previous.span.to, item.span.from).trim() === ''
      ) {
        const span = { from: previous.span.from, to: item.span.to };
        items[items.length - 1] = { text: this.quote(span).replace(/\s+/gu, ' '), quoted: false, span };
        continue;
      }
      items.push(item);
    }
    return combine('and', items);
  }

  private parseNot(): QueryNode | undefined {
    const token = this.peek();
    if (token?.kind !== 'not') return this.parsePrimary();
    this.next();
    if (this.atEndOfGroup() || this.peek()?.kind === 'and') {
      this.hint('operator_dangling', token.span, true);
      return undefined;
    }
    const operand = this.parseNot();
    if (operand === undefined) return undefined;
    return { not: operand, span: { from: token.span.from, to: operand.span.to } };
  }

  private parsePrimary(): QueryNode | undefined {
    const token = this.peek() as Token;
    switch (token.kind) {
      case 'lparen': {
        this.next();
        const inner = this.parseOr();
        const closing = this.peek();
        if (closing?.kind === 'rparen') {
          this.next();
          if (inner === undefined) this.hint('parens_empty', { from: token.span.from, to: closing.span.to }, true);
        } else {
          this.hint('paren_unclosed', token.span, true);
        }
        return inner;
      }
      case 'phrase': {
        this.next();
        if (!token.closed) this.hint('quote_unclosed', token.span, true);
        return { text: token.text, quoted: true, span: token.span };
      }
      case 'field':
        return this.parseField(token);
      default:
        return this.parseText();
    }
  }

  /** Zusammenhaengende Woerter sind EIN Freitext (E4 c). */
  private parseText(): TextNode {
    const first = this.next() as Token;
    let last = first;
    while (this.peek()?.kind === 'word' || this.peek()?.kind === 'cmp') {
      last = this.next() as Token;
    }
    const span = { from: first.span.from, to: last.span.to };
    return { text: this.quote(span).replace(/\s+/gu, ' '), quoted: false, span };
  }

  private parseField(token: Extract<Token, { kind: 'field' }>): QueryNode | undefined {
    this.next();
    const known = fieldByName(token.name) !== undefined || isLaterField(token.name);
    const following = this.peek();
    const adjacent = following !== undefined && following.span.from === token.span.to;
    if (!known) {
      // Ein unbekanntes Wort mit Doppelpunkt: Tippfehler eines Feldes sperrt
      // (8.6.1, keine Ausfuehrung), alles andere bleibt Freitext.
      if (adjacent && following.kind !== 'lparen') this.next();
      const valueEnd = this.consumeWords(this.tokens[this.position - 1]?.span.to ?? token.span.to);
      const span = { from: token.span.from, to: valueEnd };
      const similar = similarFieldName(token.name);
      if (similar !== undefined) {
        this.hints.push({
          code: 'field_unknown',
          span: { from: token.span.from, to: token.span.to - 1 },
          quote: token.name,
          blocking: true,
          suggestion: similar,
        });
      } else {
        this.hint('colon_text', span, false);
      }
      return { text: this.quote(span).replace(/\s+/gu, ' '), quoted: false, span };
    }
    // „Stichwort: Lyrik“ mit Leerzeichen meint dasselbe wie `stichwort:Lyrik`
    // (Michael, M-02). Nur der Alias „verlag“ bleibt Freitext: „Verlag: Nord“
    // ist eher ein Firmenname als eine Angabe.
    const spaced = !adjacent && (following?.kind === 'word' || following?.kind === 'phrase' || following?.kind === 'lparen' || following?.kind === 'cmp') && fold(token.name) !== 'verlag';
    if ((!adjacent && !spaced) || following === undefined || following.kind === 'rparen' || following.kind === 'or' || following.kind === 'and') {
      // „Verlag: Nord“ ist eher ein Firmenname als eine Angabe ohne Wert.
      if (following !== undefined && !adjacent && following.kind === 'word') {
        const text = this.parseTextFrom(token.span.from);
        this.hint('colon_text', text.span, false);
        return text;
      }
      this.hints.push({
        code: 'value_missing',
        span: token.span,
        quote: this.quote(token.span),
        blocking: true,
        subject: token.name,
      });
      return undefined;
    }
    let op: FieldNode['op'] = 'equals';
    if (following.kind === 'cmp') {
      op = following.op;
      this.next();
    }
    const values: QueryValue[] = [];
    let end = this.peek()?.span.to ?? token.span.to;
    const start = this.peek();
    if (start?.kind === 'lparen') {
      this.next();
      // Zwischen zwei Werten steht ODER; fehlt es, wird nicht still ODER
      // daraus, sondern ein Hinweis mit Knopf.
      let expectsValue = true;
      let lastOr: Span | undefined;
      for (;;) {
        const value = this.peek();
        if (value === undefined || value.kind === 'rparen') {
          // Ein ODER vor der schliessenden Klammer liesse eine Seite still weg.
          if (lastOr !== undefined && expectsValue) this.hint('operator_dangling', lastOr, true);
          break;
        }
        if (value.kind === 'or') {
          this.next();
          if (expectsValue) this.hint('operator_dangling', value.span, true);
          expectsValue = true;
          lastOr = value.span;
          continue;
        }
        if (value.kind === 'word' || value.kind === 'phrase') {
          this.next();
          if (value.kind === 'phrase' && !value.closed) this.hint('quote_unclosed', value.span, true);
          const previous = values[values.length - 1];
          if (!expectsValue && previous !== undefined) {
            const span = { from: previous.span.from, to: value.span.to };
            this.hints.push({
              code: 'separator_missing',
              span,
              quote: this.quote(span),
              blocking: true,
              suggestion: `${this.quote(previous.span)} ODER ${this.quote(value.span)}`,
            });
          }
          values.push(this.valueOf(value));
          end = value.span.to;
          expectsValue = false;
          continue;
        }
        // UND, NICHT, Felder oder Klammern in einer Werteliste.
        this.next();
        this.hint('operator_dangling', value.span, true);
      }
      const closing = this.peek();
      if (closing?.kind === 'rparen') {
        this.next();
        end = closing.span.to;
      } else {
        this.hint('paren_unclosed', start.span, true);
      }
      if (values.length === 0) {
        this.hints.push({ code: 'value_missing', span: token.span, quote: this.quote(token.span), blocking: true, subject: token.name });
        return undefined;
      }
    } else if (start?.kind === 'word' || start?.kind === 'phrase') {
      this.next();
      if (start.kind === 'phrase' && !start.closed) this.hint('quote_unclosed', start.span, true);
      values.push(this.valueOf(start));
      end = start.span.to;
    } else {
      this.hints.push({ code: 'value_missing', span: token.span, quote: this.quote(token.span), blocking: true, subject: token.name });
      return undefined;
    }
    return {
      field: fieldByName(token.name)?.key ?? token.name.toLocaleLowerCase('de-DE'),
      fieldSpelling: token.name,
      op,
      values,
      span: { from: token.span.from, to: end },
    };
  }

  private parseTextFrom(from: number): TextNode {
    let last: Token | undefined;
    while (this.peek()?.kind === 'word') last = this.next();
    const span = { from, to: last?.span.to ?? from };
    return { text: this.quote(span).replace(/\s+/gu, ' '), quoted: false, span };
  }

  /** Verbraucht folgende Woerter des Freitexts und liefert das Ende. */
  private consumeWords(end: number): number {
    let last = end;
    while (this.peek()?.kind === 'word' || this.peek()?.kind === 'cmp') last = (this.next() as Token).span.to;
    return last;
  }

  private valueOf(token: Extract<Token, { kind: 'word' | 'phrase' }>): QueryValue {
    return token.kind === 'phrase'
      ? { value: token.text, quoted: true, span: token.span }
      : { value: this.quote(token.span), quoted: false, span: token.span };
  }
}

function combine(kind: 'and' | 'or', parts: readonly QueryNode[]): QueryNode | undefined {
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];
  const span = { from: parts[0]!.span.from, to: parts[parts.length - 1]!.span.to };
  return kind === 'and' ? { and: parts, span } : { or: parts, span };
}

/**
 * Laenge eines Suchtexts, die der Parser liest -- dieselbe Grenze wie das
 * Suchfeld (maxLength). Sie haelt auch die Rekursion klein: 500 Klammern
 * sind rund 2.000 Aufrufe, weit unter der Stapelgrenze; ein laengerer Text
 * aus der Zwischenablage wird nicht gelesen, sondern zurueckgewiesen.
 */
export const MAXIMUM_QUERY_LENGTH = 500;

/** Liest einen Suchtext. Wirft nie; jede Eingabe ergibt Baum (oder nichts) und Hinweise. */
export function parseQuery(source: string): ParseResult {
  if (source.length > MAXIMUM_QUERY_LENGTH) {
    const span = { from: MAXIMUM_QUERY_LENGTH, to: source.length };
    return { tree: undefined, hints: [{ code: 'query_too_long', span, quote: '', blocking: true }] };
  }
  const parser = new Parser(source, lex(source));
  const tree = parser.parseAll();
  return { tree, hints: [...parser.hints].sort((a, b) => a.span.from - b.span.from) };
}
